import { Alert } from '@mui/material';
import { cloneDeep, random } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { DocumentOverview } from '../../Document/DocumentOverview';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipData } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipData';
import { BrokerClause } from './BrokerClause';
import { usePurchaseContractMeta } from './hooks/usePurchaseContractMeta';
import { PartnerIdentifiers } from './PartnerIdentifiers';
import { PurchaseContractMembers } from './PurchaseContractMembers';
import { PurchaseContractRealEstatesMain } from './PurchaseContractRealEstatesMain';
import { updateNames } from './utilities';
import { LoadingSpinner } from '../../core/LoadingSpinner';

interface IProps {
  relationship: ILegalTransactionRelationship;
  setRelationship: (newValue: ILegalTransactionRelationship) => void;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
}

const emptyData = {
  withThirdPartyClause: false,
  ThirdPartyClauseText: null,
} as const satisfies ILegalTransactionRelationshipData;

export const PurchaseContractMain: React.FC<IProps> = ({
  relationship,
  setRelationship,
  takenLegalClients,
  setTakenLegalClients,
}) => {
  const {
    isLoading,
    isNoData,
    propertyTypes,
    inventoryOptions,
    realEstateUseTypes,
    purchaseContractRelationTypes,
  } = usePurchaseContractMeta();

  const { DocumentArray, Members, data } = relationship;
  console.log('Members :', Members);

  // Cannot use memo because of memory unknown issue which does not provide live updates of the client names
  const takenBuyer = cloneDeep(Members)
    .filter(m => m.idRelationshipRole === 1)
    .flatMap(m => m.LegalClient);

  const takenSeller = cloneDeep(Members)
    .filter(m => m.idRelationshipRole === 2)
    .flatMap(m => m.LegalClient);

  const identifiers = useMemo(
    () => relationship.PartnerIdentifiers.map(i => i.PartnerIdentifier),
    [relationship.PartnerIdentifiers]
  );

  const updateRelationshipMembers = useCallback(
    (newValue: ILegalClient[], membersRelationshipRoleId: number) =>
      setRelationship({
        ...relationship,
        Members: updateNames(
          [
            ...relationship.Members.filter(
              m => m.idRelationshipRole !== membersRelationshipRoleId
            ),
            ...newValue.map(m => ({
              idRelationship: relationship.idRelationship,
              idRelationshipRole: membersRelationshipRoleId,
              idLegalClient: m.idLegalClient,
              FirstName: m.FirstName || '',
              LastName: m.LastName,
              LegalClient: m,
              LegalTransactionArray: [],
              isActiveLegalClientRelationship: true,
              idLegalClientRelationship:
                relationship.Members.find(
                  x => x.idLegalClient === m.idLegalClient
                )?.idLegalClientRelationship ??
                -random(1, Number.MAX_SAFE_INTEGER),
            })),
          ],
          newValue
        ),
      }),
    [relationship, setRelationship]
  );

  if (isLoading) return <LoadingSpinner height='50vh' />;

  if (isNoData)
    return <Alert severity='error'>Es ist ein Fehler aufgetreten!</Alert>;

  return (
    <>
      <PurchaseContractRealEstatesMain
        takenLegalClients={takenLegalClients}
        setTakenLegalClients={setTakenLegalClients}
        relationship={relationship}
        setRelationship={setRelationship}
        propertyTypes={propertyTypes!}
        inventoryOptions={inventoryOptions!}
        realEstateUseTypes={realEstateUseTypes!}
        purchaseContractRelationTypes={purchaseContractRelationTypes!}
      />
      <PurchaseContractMembers
        title='Käufer/innen'
        members={takenBuyer}
        setMembers={newValue => updateRelationshipMembers(newValue, 1)}
        takenLegalClients={takenLegalClients}
        setTakenLegalClients={setTakenLegalClients}
      />
      <PurchaseContractMembers
        title='Verkäufer/innen'
        members={takenSeller}
        setMembers={newValue => updateRelationshipMembers(newValue, 2)}
        takenLegalClients={takenLegalClients}
        setTakenLegalClients={setTakenLegalClients}
      />
      <DocumentOverview
        allowRemove
        allowUpload
        documentArray={DocumentArray}
        setDocumentArray={newDocuments =>
          setRelationship({
            ...relationship,
            DocumentArray: newDocuments.map(d => ({
              ...d,
              DocumentPurpose: 'Dokument KV',
            })),
          })
        }
        title='Bereitgestellte Daten'
        titleVariant='h6'
        marginTop={5}
      />
      <BrokerClause
        data={data ?? emptyData}
        updateRelationshipData={newValue =>
          setRelationship({
            ...relationship,
            data: newValue,
          })
        }
      />
      <PartnerIdentifiers identifiers={identifiers} />
    </>
  );
};
